import 'core-js';
import { createBrowserHistory } from 'history';
import { render } from 'react-dom';
import * as ReactGA from 'react-ga';
import * as qs from 'qs';
import * as Router from 'universal-router';
import createStore from './core/createStore';
import GraphQLClient from './core/adapter/graphQLClient';
import HttpClient from './core/adapter/httpClient';
import Dependency from './core/dependency';
import observeStore from './core/observeStore';
import routes from './core/routes';

/* Services */
import BugsnagService from './service/bugsnagService';
import SessionRepository from './service/sessionRepository/sessionRepository';
import StoreRepository from './service/storeRepository';
import OrderRepository from './service/orderRepository';
import ProductRepository from './service/productRepository';
import ProductCategoryRepository from './service/productCategoryRepository';
import ProductOrderRepository from './service/productOrderRepository';
import ValidateRepository from './service/validateRepository';
import UserRepository from './service/userRepository';

/* Usecases */
import Authentication from './usecase/authentication/authentication';
import Invite from './usecase/authentication/invite';
import Signup from './usecase/authentication/signup';

import ContentSelect from './usecase/editOrder/contentSelect';
import Cart from './usecase/editOrder/cart';
import Payment from './usecase/editOrder/payment';
import Confirm from './usecase/editOrder/confirm';
import BuyProduct from './usecase/editOrder/buyProduct';

import EditUser from './usecase/editUser';

import * as moment from 'moment';
import 'moment/locale/ja';

const DEFAULT_APP_NAME = 'gochisoo-ec-app';
const DEFAULT_PATHNAME = '/form';

async function gochisooMain() {
  const GRAPHQL_END_POINT_URL = process.env.GRAPHQL_END_POINT_URL as string;
  const BUGSNAG_API_KEY = process.env.BUGSNAG_API_KEY as string;
  const NOTIFY_RELEASE_STAGES = process.env.NOTIFY_RELEASE_STAGES as any;
  const NODE_ENV = process.env.NODE_ENV as string;
  const REVISION = process.env.REVISION as string;
  const GA_TRACKING_ID = process.env.GA_TRACKING_ID as string;
  const RESTFUL_END_POINT_URL = process.env.RESTFUL_END_POINT_URL as string;

  if (GA_TRACKING_ID) {
    ReactGA.initialize(GA_TRACKING_ID);
  }
  const defaultKey = qs.parse(window.location.search.slice(1))['gochisoo_key'];
  const pathnames = {
    login: '/login',
    invite: '/invite',
    signup: '/signup',
    form: '/form',
    cart: '/cart',
    payment: '/payment',
    paymentDelivery: '/payment/delivery',
    paymentTakeout: '/payment/takeout',
    confirm: '/confirm',
    headerUser: '/header/user',
    mypage: '/user/edit',
    shipMethodChangeParts: '/parts/buttons/ship-method',
    productCard: '/parts/card/product',
  };
  const elementName = defaultKey || DEFAULT_APP_NAME;

  const htmlElements = document.getElementsByName(elementName);
  if (!htmlElements || htmlElements.length === 0) {
    return;
  }

  const history = createBrowserHistory();
  const router = new Router(routes);
  const manageId = qs.parse(window.location.search.slice(1))['manage_id'] || htmlElements[0].getAttribute('manage-id');

  // initialize adapters
  const graphqlClient = new GraphQLClient({ url: GRAPHQL_END_POINT_URL, manageId });
  const httpClient = new HttpClient({ baseUrl: RESTFUL_END_POINT_URL, manageId });

  // initialize services
  const bugsnagService = new BugsnagService({
    appId: BUGSNAG_API_KEY,
    environment: NODE_ENV,
    revision: REVISION,
    notifyReleaseStages: NOTIFY_RELEASE_STAGES,
  });

  // repository
  const sessionRepository = new SessionRepository({ graphqlClient, httpClient, storage: window.localStorage });
  const store = createStore({});

  const storeRepository = new StoreRepository({ graphqlClient });
  const orderRepository = new OrderRepository({ graphqlClient });
  const productRepository = new ProductRepository({ graphqlClient });
  const productCategoryRepository = new ProductCategoryRepository({ graphqlClient });
  const validateRepository = new ValidateRepository({ graphqlClient });
  const userRepository = new UserRepository({ graphqlClient });
  const productOrderRepository = new ProductOrderRepository({ graphqlClient });

  bugsnagService.install();

  const dependency: Dependency = {
    history,
    authentication: new Authentication({
      storeRepository,
      userRepository,
      sessionRepository,
      bugsnagService,
      store,
    }),
    invite: new Invite({
      userRepository,
      store,
    }),
    signup: new Signup({
      userRepository,
      store,
    }),
    contentSelect: new ContentSelect({
      orderRepository,
      productRepository,
      productCategoryRepository,
      store,
    }),
    cart: new Cart({
      orderRepository,
      productRepository,
      store,
    }),
    confirm: new Confirm({
      orderRepository,
      productRepository,
      store,
    }),
    payment: new Payment({
      orderRepository,
      validateRepository,
      store,
    }),
    buyProduct: new BuyProduct({
      orderRepository,
      productRepository,
      productOrderRepository,
      store,
    }),
    editUser: new EditUser({
      userRepository,
      store,
    }),
  };

  const rerender = async () => {
    const state = store.getState();

    await htmlElements.forEach(async (htmlElement) => {
      const gochisooPathname = qs.parse(window.location.search.slice(1))['gochisoo_pathname'] || htmlElement.getAttribute('gochisoo-pathname');
      const pathname = pathnames[gochisooPathname || ''] || DEFAULT_PATHNAME;

      if (GA_TRACKING_ID) {
        ReactGA.set({ page: pathname });
        ReactGA.pageview(pathname);
      }

      const element = await router.resolve({
        path: pathname,
        props: {
          state,
          dependency,
          location: history.location,
          attrs: htmlElement.attributes,
        },
      });

      render(element, htmlElement);
    })
  };

  await dependency.authentication
    .getSession()
    .catch(err => console.error(err));

  moment.locale('ja');
  observeStore(store, rerender);
  history.listen(rerender);

  rerender();
}

document.addEventListener('DOMContentLoaded', gochisooMain);
